import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  ExternalLink,
  FileText,
  Loader2,
  Mail,
  MapPin,
  Megaphone,
  Pencil,
  Phone,
  Search,
  UserRound,
  X,
} from 'lucide-react';
import { useMyCampaignsQuery } from '@/features/campaigns/api/useCampaignQueries';
import { CampaignListCard } from '@/features/campaigns/components/CampaignListCard';
import { CAMPAIGN_STATUS_OPTIONS } from '@/features/campaigns/utils/campaignListUtils';
import { formatMobileWithCountryCode } from '@/features/auth/utils/mobileNumber';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { fetchClientById } from '../api/clientsApi';

type TabKey = 'overview' | 'campaigns';

const TABS: { key: TabKey; label: string; icon: typeof UserRound }[] = [
  { key: 'overview', label: 'Overview', icon: UserRound },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
];

const PAGE_SIZE_OPTIONS = [15, 30, 60, 100] as const;

function getDocumentUrl(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') {
    return value.startsWith('http') ? value.trim() : '';
  }
  if (typeof value === 'object') {
    const doc = value as { url?: string; mediaUrl?: string; fileUrl?: string };
    return String(doc.url || doc.mediaUrl || doc.fileUrl || '').trim();
  }
  return '';
}

function truncateChars(value: unknown, max = 50) {
  const text = String(value ?? '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max)}…`;
}

function formatRole(role?: string) {
  if (!role) return 'Client';
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function Item({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value?: string | null;
}) {
  return (
    <div className="rounded-xl bg-[var(--dash-soft)] p-3.5 text-left">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        {icon}
        {label}
      </div>
      <p className="m-0 text-sm font-medium break-words text-[var(--dash-ink)]">
        {value?.trim() ? value : '—'}
      </p>
    </div>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = (searchParams.get('tab') as TabKey) || 'overview';
  const setTab = (nextTab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
  };

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] =
    useState<(typeof PAGE_SIZE_OPTIONS)[number]>(15);
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status, pageSize, id]);

  const q = useQuery({
    queryKey: queryKeys.clients.detail(id || ''),
    queryFn: () => fetchClientById(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });


  const campaignFilters = useMemo(
    () => ({
      page,
      limit: pageSize,
      search: debouncedSearch,
      status,
      agencyClientId: id || '',
    }),
    [page, pageSize, debouncedSearch, status, id],
  );

  const campaignsQuery = useMyCampaignsQuery(campaignFilters, {
    enabled: Boolean(id) && tab === 'campaigns',
  });

  console.log('data.data', q.data?.data);
  const user = q.data?.data?.client || q.data?.data?.user || q.data?.data || {};
  const displayName =
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.companyName ||
    'Client';
  const initials =
    `${user.firstName?.[0] || ''}${user.lastName?.[0] || user.companyName?.[0] || 'C'}`.toUpperCase();
  const phone = formatMobileWithCountryCode(
    user.mobileNumber,
    user.mobileCountryCode,
  );
  const address =
    user.fullAddress ||
    user.address?.fullAddress ||
    (typeof user.address === 'string' ? user.address : '') ||
    '';
  const pin = user.pincode || user.pin || user.address?.pincode || '';
  const clientStatus = String(
    user.status || user.isAdminVerified || '',
  ).toUpperCase();
  const isVerified = clientStatus === 'VERIFIED';
  const documents =
    user.documents && typeof user.documents === 'object'
      ? (user.documents as Record<string, unknown>)
      : {};


  console.log('documents', documents);
  const authorizationLetterUrl =
    getDocumentUrl(documents.entity) ||
    getDocumentUrl(documents.agreementFile) ||
    getDocumentUrl(documents.authorizationLetter);

  const clientCampaigns = campaignsQuery.data?.data?.campaigns ?? [];
  const campaignsPagination = campaignsQuery.data?.data?.pagination;
  const campaignsTotalPages = campaignsPagination?.totalPages || 1;
  const campaignsTotalCount =
    campaignsPagination?.totalCount ??
    campaignsPagination?.total ??
    clientCampaigns.length;
  const hasCampaignFilters = Boolean(debouncedSearch) || status !== 'All';
  const rangeStart =
    campaignsTotalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, campaignsTotalCount);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/clients')}
        className="inline-flex w-fit items-center gap-1.5 text-sm font-medium text-[var(--dash-muted)] transition hover:text-[var(--dash-ink)]"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to clients
      </button>

      {q.isPending ? (
        <div className="flex justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-[var(--dash-brand)]" />
        </div>
      ) : (
        <div className="camp-fade-up flex flex-col gap-5">
          <div className="relative overflow-hidden rounded-2xl">
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(135deg, #145E8C 0%, #007A78 40%, #33A65B 100%)',
              }}
            />
            <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-7 text-white sm:flex-row sm:gap-6 sm:px-8 sm:py-8">
              <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-full border-[3px] border-white/70 bg-white/20 text-2xl font-bold backdrop-blur-sm">
                {initials}
              </div>
              <div className="flex flex-1 flex-col items-center gap-1.5 text-center sm:items-start sm:text-left">
                <h1
                  className="m-0 max-w-full text-[22px] font-bold leading-tight"
                  title={user.companyName || displayName}
                >
                  {truncateChars(user.companyName || displayName, 50)}
                </h1>
                <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  <span className="rounded-full border border-white/25 bg-white/10 px-3 py-[3px] text-[10px] font-bold tracking-widest uppercase backdrop-blur-sm">
                    {formatRole(user.role)}
                  </span>
                  {clientStatus ? (
                    <span
                      className={`rounded-full px-2.5 py-[3px] text-[10px] font-bold uppercase ${
                        isVerified
                          ? 'bg-emerald-400/20 text-emerald-100'
                          : 'bg-amber-400/20 text-amber-100'
                      }`}
                    >
                      {isVerified ? 'Verified' : 'Not verified'}
                    </span>
                  ) : null}
                  {!isVerified && clientStatus && clientStatus !== 'REJECTED' ? (
                    <span className="rounded-full bg-amber-400/20 px-2.5 py-[3px] text-[10px] font-bold uppercase text-amber-100">
                      Already active with another agency
                    </span>
                  ) : null}
                </div>
                {!isVerified && clientStatus && clientStatus !== 'REJECTED' ? (
                  <p className="m-0 mt-1 max-w-xl text-sm text-amber-50/95">
                    This company is already active with another agency. Admin
                    approval is needed before this client can be used for
                    campaigns.
                  </p>
                ) : null}
                {user.companyName && displayName !== user.companyName ? (
                  <p className="m-0 mt-1 text-sm text-white/85">
                    Contact: {displayName}
                  </p>
                ) : null}
                <div className="mt-1 flex flex-wrap items-center justify-center gap-4 text-[13px] opacity-90 sm:justify-start">
                  {user.email ? (
                    <span className="flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 opacity-70" />
                      {user.email}
                    </span>
                  ) : null}
                  {phone ? (
                    <span className="flex items-center gap-1.5">
                      <Phone className="h-3.5 w-3.5 opacity-70" />
                      {phone}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => id && navigate(`/clients/${id}/edit`)}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/90 px-4 text-xs font-bold tracking-wide text-[var(--dash-brand)] uppercase transition hover:bg-white"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
                {user.email ? (
                  <a
                    href={`mailto:${user.email}`}
                    className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/90 px-4 text-xs font-bold tracking-wide text-[var(--dash-brand)] uppercase no-underline transition hover:bg-white"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    Email
                  </a>
                ) : null}
                {phone ? (
                  <a
                    href={`tel:${`${user.mobileCountryCode || ''}${String(user.mobileNumber || '').replace(/\D/g, '')}`}`}
                    className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-white/90 px-4 text-xs font-bold tracking-wide text-[var(--dash-brand)] uppercase no-underline transition hover:bg-white"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    Call
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <nav className="flex gap-1.5 overflow-x-auto rounded-2xl border border-[var(--dash-line)] bg-white p-1.5 shadow-sm">
            {TABS.map((t) => {
              const active = tab === t.key;
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold whitespace-nowrap transition ${
                    active
                      ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                      : 'text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] hover:text-[var(--dash-ink)]'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {t.label}
                </button>
              );
            })}
          </nav>

          {tab === 'overview' ? (
            <div className="flex flex-col gap-5">
              <section className="camp-section">
                <header className="mb-4 flex items-center gap-2 border-b border-[var(--dash-line)] pb-3 text-left">
                  <Building2 className="h-4 w-4 text-[var(--dash-brand)]" />
                  <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">
                    Business
                  </h2>
                </header>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Item label="Client Name" value={user.companyName} />
                  <Item
                    label="Category of Business"
                    value={user.businessCategory}
                  />
                  <Item
                    label="Sub Category of Business"
                    value={user.businessSubCategory}
                  />
                  <Item label="Contact Person" value={displayName} />
                </div>
              </section>

              <section className="camp-section">
                <header className="mb-4 flex items-center gap-2 border-b border-[var(--dash-line)] pb-3 text-left">
                  <MapPin className="h-4 w-4 text-[var(--dash-brand)]" />
                  <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">
                    Address
                  </h2>
                </header>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                  <div className="sm:col-span-3">
                    <Item label="Address of Client" value={address} />
                  </div>
                  <Item label="PIN" value={pin} />
                </div>
              </section>

              <section className="camp-section">
                <header className="mb-4 flex items-center gap-2 border-b border-[var(--dash-line)] pb-3 text-left">
                  <FileText className="h-4 w-4 text-[var(--dash-brand)]" />
                  <h2 className="m-0 text-base font-semibold text-[var(--dash-ink)]">
                    Documents
                  </h2>
                </header>
                {authorizationLetterUrl ? (
                  <a
                    href={authorizationLetterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-xl bg-[var(--dash-soft)] p-3.5 text-left no-underline transition hover:bg-[var(--dash-line)]/40"
                  >
                    <FileText className="h-5 w-5 shrink-0 text-[var(--dash-brand)]" />
                    <div className="min-w-0 flex-1">
                      <p className="m-0 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
                        Authorization Letter
                      </p>
                      <p className="m-0 mt-0.5 text-sm font-medium text-[var(--dash-ink)]">
                        View PDF
                      </p>
                    </div>
                    <ExternalLink className="h-4 w-4 shrink-0 text-[var(--dash-muted)]" />
                  </a>
                ) : (
                  <p className="m-0 text-sm text-[var(--dash-muted)]">
                    No authorization letter uploaded
                  </p>
                )}
              </section>
            </div>
          ) : null}

          {tab === 'campaigns' ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-white p-4 shadow-[0_10px_28px_-22px_rgba(16,38,73,0.35)]">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="relative flex-1 md:max-w-sm">
                    <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[var(--dash-muted)]" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search Order ID, Name"
                      className="w-full rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] py-2.5 pr-10 pl-10 text-sm outline-none transition focus:border-[var(--dash-brand)] focus:bg-white focus:ring-2 focus:ring-[var(--dash-brand)]/15"
                    />
                    {search ? (
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="absolute top-1/2 right-2.5 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(15,27,45,0.08)] text-[var(--dash-muted)] transition hover:bg-[rgba(15,27,45,0.14)] hover:text-[var(--dash-ink)]"
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    ) : null}
                  </div>

                  {hasCampaignFilters ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setStatus('All');
                      }}
                      className="text-xs font-semibold text-red-500 hover:underline sm:ml-auto"
                    >
                      Clear all
                    </button>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {CAMPAIGN_STATUS_OPTIONS.slice(0, 6).map((opt) => {
                    const active = status === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setStatus(opt.value)}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                          active
                            ? 'bg-[var(--dash-brand)] text-white'
                            : 'bg-[var(--dash-soft)] text-[var(--dash-muted)] hover:text-[var(--dash-ink)]'
                        }`}
                      >
                        {opt.label.replace(' Status', '')}
                      </button>
                    );
                  })}
                </div>
              </div>

              {campaignsQuery.isPending ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: Math.min(pageSize, 9) }).map((_, i) => (
                    <div
                      key={i}
                      className="camp-skeleton h-[220px] rounded-2xl border border-[var(--dash-line)]"
                    />
                  ))}
                </div>
              ) : clientCampaigns.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[var(--dash-line)] bg-white px-6 py-12 text-center">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
                    <Megaphone className="h-6 w-6" />
                  </div>
                  <p className="m-0 text-sm font-semibold text-[var(--dash-ink)]">
                    {hasCampaignFilters
                      ? 'No matching campaigns'
                      : 'No campaigns yet'}
                  </p>
                  <p className="mt-1 mb-0 max-w-sm text-sm text-[var(--dash-muted)]">
                    {hasCampaignFilters
                      ? 'Try clearing filters or searching a different name.'
                      : 'Campaigns linked to this client will show up here once created.'}
                  </p>
                  {hasCampaignFilters ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSearch('');
                        setStatus('All');
                      }}
                      className="mt-4 rounded-xl bg-[var(--dash-brand)] px-4 py-2 text-sm font-semibold text-white"
                    >
                      Clear filters
                    </button>
                  ) : null}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {clientCampaigns.map((campaign) => (
                      <CampaignListCard
                        key={campaign.campaignId || campaign._id}
                        campaign={campaign}
                        onView={(cid) => navigate(`/campaigns/${cid}`)}
                      />
                    ))}
                  </div>

                  <div className="flex flex-col gap-3 rounded-2xl border border-[var(--dash-line)] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="m-0 text-left text-sm text-[var(--dash-muted)]">
                      Showing{' '}
                      <span className="font-semibold text-[var(--dash-ink)]">
                        {rangeStart}–{rangeEnd}
                      </span>{' '}
                      of{' '}
                      <span className="font-semibold text-[var(--dash-ink)]">
                        {campaignsTotalCount}
                      </span>
                    </p>

                    <div className="flex flex-wrap items-center gap-3">
                      <label className="inline-flex items-center gap-2 text-sm text-[var(--dash-muted)]">
                        Per page
                        <select
                          value={pageSize}
                          onChange={(e) =>
                            setPageSize(
                              Number(
                                e.target.value,
                              ) as (typeof PAGE_SIZE_OPTIONS)[number],
                            )
                          }
                          className="rounded-lg border border-[var(--dash-line)] bg-[var(--dash-soft)] px-2.5 py-1.5 text-sm font-semibold text-[var(--dash-ink)] outline-none focus:border-[var(--dash-brand)]"
                          aria-label="Campaigns per page"
                        >
                          {PAGE_SIZE_OPTIONS.map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                      </label>

                      {campaignsTotalPages > 1 ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            className="rounded-xl border border-[var(--dash-line)] bg-white px-3.5 py-1.5 text-sm font-semibold disabled:opacity-40"
                          >
                            Previous
                          </button>
                          <span className="min-w-[4.5rem] text-center text-sm text-[var(--dash-muted)]">
                            {page} / {campaignsTotalPages}
                          </span>
                          <button
                            type="button"
                            disabled={page >= campaignsTotalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="rounded-xl border border-[var(--dash-line)] bg-white px-3.5 py-1.5 text-sm font-semibold disabled:opacity-40"
                          >
                            Next
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
